import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { backgroundOptions } from '../common/backgrounds';

const { 
  FiUser, FiMail, FiMapPin, FiLinkedin, FiTwitter, FiGithub, FiLayout, 
  FiType, FiImage, FiChevronDown, FiPlus, FiTrash2, FiLink, FiYoutube, FiFacebook, FiInstagram, FiZap, FiRefreshCw, FiHelpCircle
} = FiIcons;

// Expanded Personas List (20 Total)
const quickPersonas = [
  { id: 'ux', label: 'UX Designer', data: { name: 'Alex Morgan', title: 'Senior UX Designer', about: 'Crafting intuitive digital experiences that solve complex user problems.', learning: ['Figma', 'Prototyping', 'User Research'], colors: { background: '#000000', card: '#121212', text: '#ffffff', accent: '#ec4899' }, backgroundId: 'aurora' } },
  { id: 'dev', label: 'Web Developer', data: { name: 'Jordan Lee', title: 'Full Stack Developer', about: 'Building scalable web applications with modern technologies.', learning: ['React', 'Node.js', 'TypeScript', 'Next.js'], colors: { background: '#050505', card: '#0a0a0a', text: '#e2e8f0', accent: '#3b82f6' }, backgroundId: 'grid' } },
  { id: 'music', label: 'AI Music Artist', data: { name: 'Dadda Cool', title: 'AI Sound Architect', about: 'Merging algorithmic composition with human emotion.', learning: ['Suno', 'Udio', 'Python', 'Ableton'], colors: { background: '#0f0c29', card: '#1a1a2e', text: '#ffffff', accent: '#8b5cf6' }, backgroundId: 'galaxy' } },
  { id: 'indie', label: 'Indie Hacker', data: { name: 'Sarah Ship', title: 'Indie Maker & SaaS Founder', about: 'Shipping one startup a month. Building in public.', learning: ['Marketing', 'SEO', 'SaaS', 'Stripe'], colors: { background: '#111', card: '#18181b', text: '#fff', accent: '#10b981' }, backgroundId: 'matrix' } },
  { id: 'creator', label: 'Content Creator', data: { name: 'Chris Cam', title: 'Tech Youtuber', about: 'Reviewing the latest tech and coding tutorials for 100k+ subs.', learning: ['Editing', 'Scripting', 'Camera Gear'], colors: { background: '#000', card: '#1c1917', text: '#fff', accent: '#f59e0b' }, backgroundId: 'particles' } },
  { id: 'crypto', label: 'Crypto Trader', data: { name: 'Token Tom', title: 'DeFi Strategist', about: 'Navigating the blockchain and analyzing market trends.', learning: ['Solidity', 'Web3', 'TradingView'], colors: { background: '#000', card: '#0f172a', text: '#fff', accent: '#6366f1' }, backgroundId: 'circuit' } },
  { id: 'coach', label: 'Fitness Coach', data: { name: 'Fit Jess', title: 'Personal Trainer', about: 'Helping you achieve your dream physique through science-based training.', learning: ['Nutrition', 'Physiology', 'Motivation'], colors: { background: '#000', card: '#1a1c1e', text: '#fff', accent: '#ef4444' }, backgroundId: 'orb' } },
  { id: 'photo', label: 'Photographer', data: { name: 'Lens Luke', title: 'Visual Storyteller', about: 'Capturing moments that last a lifetime. Travel & Portrait.', learning: ['Lightroom', 'Composition', 'Color Grade'], colors: { background: '#000', card: '#18181b', text: '#fff', accent: '#f472b6' }, backgroundId: 'noir' } },
  { id: 'product', label: 'Product Manager', data: { name: 'Product Pam', title: 'Head of Product', about: 'Driving product vision from zero to one with user-centric strategies.', learning: ['Strategy', 'Roadmapping', 'Jira'], colors: { background: '#000', card: '#161618', text: '#fff', accent: '#8b5cf6' }, backgroundId: 'beams' } },
  { id: 'data', label: 'Data Scientist', data: { name: 'Data Dave', title: 'AI Researcher', about: 'Turning complex data into actionable insights and predictive models.', learning: ['Python', 'PyTorch', 'TensorFlow'], colors: { background: '#000', card: '#0f172a', text: '#fff', accent: '#0ea5e9' }, backgroundId: 'matrix' } },
  { id: 'marketer', label: 'Marketing Guru', data: { name: 'Growth Gary', title: 'CMO', about: 'Scaling brands through viral campaigns and data-driven growth hacking.', learning: ['SEO', 'Ads', 'Analytics'], colors: { background: '#000', card: '#1c1917', text: '#fff', accent: '#f97316' }, backgroundId: 'plasma' } },
  { id: '3d', label: '3D Artist', data: { name: 'Poly Paul', title: '3D Character Artist', about: 'Sculpting digital worlds and characters for games and film.', learning: ['Blender', 'ZBrush', 'Maya'], colors: { background: '#000', card: '#18181b', text: '#fff', accent: '#a855f7' }, backgroundId: 'grid' } },
  { id: 'gamedev', label: 'Game Developer', data: { name: 'Unity Una', title: 'Gameplay Programmer', about: 'Creating immersive gameplay mechanics and systems.', learning: ['C#', 'Unity', 'Unreal C++'], colors: { background: '#000', card: '#111', text: '#fff', accent: '#ef4444' }, backgroundId: 'circuit' } },
  { id: 'writer', label: 'Writer/Author', data: { name: 'Wordy Will', title: 'Copywriter & Author', about: 'Weaving compelling narratives that engage and convert.', learning: ['Storytelling', 'Copywriting', 'SEO'], colors: { background: '#000', card: '#1f1f1f', text: '#fff', accent: '#d946ef' }, backgroundId: 'aurora' } },
  { id: 'security', label: 'Cyber Security', data: { name: 'Secure Sam', title: 'Ethical Hacker', about: 'Securing networks and applications against modern threats.', learning: ['Pentesting', 'Network Sec', 'Linux'], colors: { background: '#000', card: '#0a0a0a', text: '#0f0', accent: '#00ff00' }, backgroundId: 'matrix' } },
  { id: 'video', label: 'Video Editor', data: { name: 'Cut Cathy', title: 'Film Editor', about: 'Telling stories through rhythm, pacing, and visual composition.', learning: ['Premiere', 'After Effects', 'Color'], colors: { background: '#000', card: '#18181b', text: '#fff', accent: '#facc15' }, backgroundId: 'noir' } },
  { id: 'blockchain', label: 'Blockchain Dev', data: { name: 'Chain Charlie', title: 'Smart Contract Dev', about: 'Building decentralized applications on the cutting edge of Web3.', learning: ['Solidity', 'Rust', 'Web3.js'], colors: { background: '#000', card: '#1e1b4b', text: '#fff', accent: '#6366f1' }, backgroundId: 'galaxy' } },
  { id: 'mobile', label: 'Mobile Developer', data: { name: 'App Andy', title: 'iOS & Android Dev', about: 'Crafting buttery smooth mobile experiences for millions of users.', learning: ['Swift', 'Kotlin', 'React Native'], colors: { background: '#000', card: '#171717', text: '#fff', accent: '#3b82f6' }, backgroundId: 'beams' } },
  { id: 'ui', label: 'UI Engineer', data: { name: 'Pixel Pete', title: 'Design Systems Lead', about: 'Bridging the gap between design and engineering with pixel perfection.', learning: ['Storybook', 'CSS', 'React'], colors: { background: '#000', card: '#18181b', text: '#fff', accent: '#ec4899' }, backgroundId: 'prismatic' } },
  { id: 'devops', label: 'DevOps Eng', data: { name: 'Cloud Clara', title: 'SRE', about: 'Ensuring 99.99% uptime and seamless CI/CD pipelines.', learning: ['AWS', 'Docker', 'Kubernetes'], colors: { background: '#000', card: '#0f172a', text: '#fff', accent: '#0ea5e9' }, backgroundId: 'circuit' } }
];

const AccordionItem = ({ title, icon, children, isOpen, onClick }) => (
  <div className="border border-zinc-800 rounded-xl overflow-hidden bg-black/40 backdrop-blur-sm transition-all duration-300">
    <button onClick={onClick} className="w-full flex items-center justify-between p-4 bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors text-left outline-none">
      <div className="flex items-center gap-3 text-zinc-100 font-medium">
        <SafeIcon icon={icon} className="text-zinc-400" /> {title}
      </div>
      <SafeIcon icon={FiChevronDown} className={`text-zinc-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
          <div className="p-4 space-y-4 border-t border-zinc-800/50">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

function EditorPanel({ bioData, setBioData, fileInputRef, handleAvatarUpload }) {
  const [openSection, setOpenSection] = useState('basic');

  const toggleSection = (section) => setOpenSection(openSection === section ? null : section);

  const handleInputChange = (field, value) => setBioData({ ...bioData, [field]: value });
  
  const handleSocialChange = (platform, value) => {
    setBioData({ ...bioData, socials: { ...bioData.socials, [platform]: value } });
  };

  const handleColorChange = (key, value) => {
    setBioData({ ...bioData, colors: { ...bioData.colors, [key]: value } });
  };

  const loadPersona = (e) => {
    const persona = quickPersonas.find(p => p.id === e.target.value);
    if (persona) {
      setBioData(prev => ({
        ...prev,
        ...persona.data,
        avatar: `https://i.pravatar.cc/300?u=${persona.id}`,
        avatarType: 'upload'
      }));
    }
  };

  const generateRandomColor = () => {
    return '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
  };

  const handleRandomGradient = () => {
    const start = generateRandomColor();
    const end = generateRandomColor();
    const directions = ['to bottom', 'to top', 'to right', 'to left', 'to bottom right', 'to bottom left', 'to top right', 'to top left'];
    const dir = directions[Math.floor(Math.random() * directions.length)];
    
    const newConfig = { start, end, dir };
    const gradientString = `linear-gradient(${dir}, ${start}, ${end})`;
    
    setBioData(prev => ({
      ...prev,
      gradientConfig: newConfig,
      customBackground: gradientString
    }));
  };

  // Gradient Builder Logic
  const handleGradientChange = (key, value) => {
    const newConfig = { ...bioData.gradientConfig, [key]: value };
    const gradientString = `linear-gradient(${newConfig.dir}, ${newConfig.start}, ${newConfig.end})`;
    
    setBioData(prev => ({
      ...prev,
      gradientConfig: newConfig,
      customBackground: gradientString
    }));
  };

  // Solid Color Logic
  const handleSolidChange = (value) => {
     setBioData(prev => ({
        ...prev,
        customBackground: value
     }));
  };

  // Project Management
  const addProject = () => {
    if (bioData.projects.length < 5) {
      setBioData(prev => ({ ...prev, projects: [...prev.projects, { name: '', url: '', desc: '' }] }));
    }
  };
  const updateProject = (index, field, value) => {
    const newProjects = [...bioData.projects];
    newProjects[index][field] = value;
    setBioData(prev => ({ ...prev, projects: newProjects }));
  };
  const removeProject = (index) => {
    const newProjects = bioData.projects.filter((_, i) => i !== index);
    setBioData(prev => ({ ...prev, projects: newProjects }));
  };

  return (
    <div className="space-y-4">
      {/* Quick Start Dropdown */}
      <div className="p-4 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-xl border border-blue-500/20">
        <h3 className="text-sm font-bold text-blue-200 mb-3 flex items-center gap-2">
          <SafeIcon icon={FiZap} /> Quick Start Templates ({quickPersonas.length})
        </h3>
        <div className="relative">
          <select 
            onChange={loadPersona} 
            className="w-full bg-black border border-blue-500/30 text-white rounded-lg p-3 appearance-none cursor-pointer hover:border-blue-400 transition-colors outline-none"
            defaultValue=""
          >
            <option value="" disabled>Select a persona to auto-fill...</option>
            {quickPersonas.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-400">
             <SafeIcon icon={FiChevronDown} />
          </div>
        </div>
      </div>

      <AccordionItem title="Basic Info" icon={FiUser} isOpen={openSection === 'basic'} onClick={() => toggleSection('basic')}>
        <input type="text" placeholder="Full Name" value={bioData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:border-blue-500 outline-none" />
        <input type="text" placeholder="Job Title" value={bioData.title} onChange={(e) => handleInputChange('title', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:border-blue-500 outline-none" />
        <textarea placeholder="About Yourself" rows="4" value={bioData.about} onChange={(e) => handleInputChange('about', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:border-blue-500 outline-none resize-none" />
        <div className="grid grid-cols-2 gap-3">
             <div className="p-3 bg-zinc-900 border border-zinc-700 rounded-lg flex items-center justify-between cursor-pointer hover:bg-zinc-800" onClick={() => fileInputRef.current?.click()}>
                <span className="text-sm text-zinc-400">Upload Avatar</span>
                <SafeIcon icon={FiImage} />
             </div>
        </div>
      </AccordionItem>

      <AccordionItem title="Contact & Socials" icon={FiMail} isOpen={openSection === 'social'} onClick={() => toggleSection('social')}>
        <div className="space-y-3">
            <div className="relative"><SafeIcon icon={FiMail} className="absolute left-3 top-3.5 text-zinc-500" /><input type="email" placeholder="Email Address" value={bioData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 pl-10 text-white focus:border-blue-500 outline-none" /></div>
            <div className="relative"><SafeIcon icon={FiMapPin} className="absolute left-3 top-3.5 text-zinc-500" /><input type="text" placeholder="Location" value={bioData.location} onChange={(e) => handleInputChange('location', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 pl-10 text-white focus:border-blue-500 outline-none" /></div>
            <div className="h-px bg-zinc-800 my-4"></div>
            <div className="grid grid-cols-1 gap-3">
                {['linkedin', 'twitter', 'github', 'instagram', 'youtube', 'tiktok', 'facebook', 'dribbble'].map(platform => (
                    <div key={platform} className="relative">
                        <div className="absolute left-3 top-3.5 text-zinc-500 capitalize text-xs font-bold w-16">{platform}</div>
                        <input type="text" placeholder={`${platform} URL`} value={bioData.socials[platform]} onChange={(e) => handleSocialChange(platform, e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 pl-24 text-white focus:border-blue-500 outline-none text-sm" />
                    </div>
                ))}
            </div>
        </div>
      </AccordionItem>

      <AccordionItem title="Projects (Max 5)" icon={FiLayout} isOpen={openSection === 'projects'} onClick={() => toggleSection('projects')}>
        <div className="space-y-4">
           {bioData.projects.map((proj, idx) => (
             <div key={idx} className="p-3 bg-zinc-900/50 border border-zinc-700 rounded-lg space-y-2 relative group">
                <button onClick={() => removeProject(idx)} className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><SafeIcon icon={FiTrash2} /></button>
                <input type="text" placeholder="Project Name" value={proj.name} onChange={(e) => updateProject(idx, 'name', e.target.value)} className="w-full bg-transparent border-b border-zinc-700 p-2 text-white outline-none focus:border-blue-500" />
                <input type="text" placeholder="Project URL (https://...)" value={proj.url} onChange={(e) => updateProject(idx, 'url', e.target.value)} className="w-full bg-transparent border-b border-zinc-700 p-2 text-white outline-none focus:border-blue-500 text-sm" />
                <input type="text" placeholder="Short description..." value={proj.desc} onChange={(e) => updateProject(idx, 'desc', e.target.value)} className="w-full bg-transparent p-2 text-zinc-400 outline-none text-sm" />
             </div>
           ))}
           {bioData.projects.length < 5 && (
             <button onClick={addProject} className="w-full py-2 border border-dashed border-zinc-700 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors flex items-center justify-center gap-2 text-sm">
                <SafeIcon icon={FiPlus} /> Add Project
             </button>
           )}
        </div>
      </AccordionItem>

      <AccordionItem title="Skills & Tech" icon={FiType} isOpen={openSection === 'skills'} onClick={() => toggleSection('skills')}>
         <div className="space-y-2">
            <p className="text-xs text-zinc-500">Comma separated</p>
            <textarea placeholder="React, Design, Marketing..." rows="3" value={bioData.learning.join(', ')} onChange={(e) => handleInputChange('learning', e.target.value.split(',').map(s => s.trim()))} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:border-blue-500 outline-none resize-none" />
            <input type="text" placeholder="Availability Status (e.g. Open to Work)" value={bioData.availability} onChange={(e) => handleInputChange('availability', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:border-blue-500 outline-none mt-2" />
         </div>
      </AccordionItem>

      <AccordionItem title="Theme & Background" icon={FiLayout} isOpen={openSection === 'theme'} onClick={() => toggleSection('theme')}>
        <div className="space-y-4">
           {/* Background Type Selector */}
           <div className="flex gap-2 p-1 bg-zinc-900 rounded-lg">
              {['preset', 'solid', 'gradient'].map(type => (
                 <button 
                   key={type}
                   onClick={() => handleInputChange('backgroundType', type)}
                   className={`flex-1 py-2 text-xs font-bold uppercase rounded-md transition-all ${bioData.backgroundType === type ? 'bg-zinc-700 text-white shadow' : 'text-zinc-500 hover:text-zinc-300'}`}
                 >
                   {type}
                 </button>
              ))}
           </div>

           {bioData.backgroundType === 'preset' && (
              <div className="relative">
                 <select 
                    value={bioData.backgroundId} 
                    onChange={(e) => handleInputChange('backgroundId', e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 appearance-none cursor-pointer hover:border-blue-400 transition-colors outline-none text-white"
                 >
                   {backgroundOptions.map(bg => <option key={bg.id} value={bg.id}>{bg.name}</option>)}
                 </select>
                 <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-500" />
              </div>
           )}

           {bioData.backgroundType === 'solid' && (
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded-lg border border-zinc-700">
                  <input 
                    type="color" 
                    value={bioData.customBackground || '#000000'}
                    onChange={(e) => handleSolidChange(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border-none"
                  />
                  <div className="flex-1">
                      <div className="text-xs text-zinc-500 mb-1">Pick a Color</div>
                      <div className="text-sm font-mono text-white">{bioData.customBackground || '#000000'}</div>
                  </div>
              </div>
           )}

           {bioData.backgroundType === 'gradient' && (
             <div className="space-y-3 p-3 bg-zinc-900 rounded-lg border border-zinc-700">
                <button 
                  onClick={handleRandomGradient}
                  className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-bold shadow-lg hover:shadow-blue-500/20 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiRefreshCw} /> Generate Random Gradient
                </button>

                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <label className="text-xs text-zinc-500 mb-1 block">Start Color</label>
                        <div className="flex gap-2 items-center">
                            <input type="color" value={bioData.gradientConfig?.start || '#000000'} onChange={(e) => handleGradientChange('start', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-none" />
                            <span className="text-xs font-mono">{bioData.gradientConfig?.start}</span>
                        </div>
                    </div>
                    <div className="flex-1">
                        <label className="text-xs text-zinc-500 mb-1 block">End Color</label>
                        <div className="flex gap-2 items-center">
                            <input type="color" value={bioData.gradientConfig?.end || '#000000'} onChange={(e) => handleGradientChange('end', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-none" />
                            <span className="text-xs font-mono">{bioData.gradientConfig?.end}</span>
                        </div>
                    </div>
                </div>
                <div>
                   <label className="text-xs text-zinc-500 mb-1 block">Direction</label>
                   <select 
                     value={bioData.gradientConfig?.dir || 'to bottom'} 
                     onChange={(e) => handleGradientChange('dir', e.target.value)}
                     className="w-full bg-zinc-800 border border-zinc-600 rounded p-2 text-xs text-white outline-none"
                   >
                     <option value="to bottom">To Bottom ↓</option>
                     <option value="to top">To Top ↑</option>
                     <option value="to right">To Right →</option>
                     <option value="to left">To Left ←</option>
                     <option value="to bottom right">To Bottom Right ↘</option>
                     <option value="to bottom left">To Bottom Left ↙</option>
                     <option value="to top right">To Top Right ↗</option>
                     <option value="to top left">To Top Left ↖</option>
                   </select>
                </div>
             </div>
           )}

           <div className="grid grid-cols-2 gap-3 pt-4 border-t border-zinc-800">
              <div>
                 <label className="text-xs text-zinc-500 mb-1 block">Card Color</label>
                 <div className="flex gap-2">
                    <input type="color" value={bioData.colors.card} onChange={(e) => handleColorChange('card', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-none" />
                    <input type="text" value={bioData.colors.card} onChange={(e) => handleColorChange('card', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 text-xs text-white" />
                 </div>
              </div>
              <div>
                 <label className="text-xs text-zinc-500 mb-1 block">Accent Color</label>
                 <div className="flex gap-2">
                    <input type="color" value={bioData.colors.accent} onChange={(e) => handleColorChange('accent', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-none" />
                    <input type="text" value={bioData.colors.accent} onChange={(e) => handleColorChange('accent', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 text-xs text-white" />
                 </div>
              </div>
           </div>
        </div>
      </AccordionItem>

      <AccordionItem title="FAQ & Help" icon={FiHelpCircle} isOpen={openSection === 'faq'} onClick={() => toggleSection('faq')}>
        <div className="space-y-4 text-sm text-zinc-400">
            <div>
                <h4 className="text-zinc-200 font-bold mb-1">Does my data get saved?</h4>
                <p>Your data is saved locally in your browser so you won't lose it if you refresh. It is not stored on any server. To reset everything, click the "Bio Creator" logo/icon in the top left of the header.</p>
            </div>
            <div>
                <h4 className="text-zinc-200 font-bold mb-1">How do I publish this?</h4>
                <p>Export the HTML file, rename it to index.html, and drop it onto Netlify or GitHub Pages.</p>
            </div>
        </div>
      </AccordionItem>
    </div>
  );
}

export default EditorPanel;